// Host-facing HTTP implementation of ThreeDGenApi. Firebase-free: auth + base URL
// come from the injected BackendHttpDeps. Do NOT import from barrel exports
// (threeDGen/index.ts).

import {
  ThreeDGenApi,
  ThreeDGenParams,
  ThreeDGenResult,
  RemeshParams,
  RemeshResult,
  RigParams,
  RigResult,
  AnimateParams,
  AnimateResult,
  ThreeDGenJobEvent,
} from './ThreeDGenApi'
import { RundotApiError } from '../errors'
import { resolveGameSessionToken } from '../http/backendHttpDeps'
import type { BackendHttpDeps } from '../http/backendHttpDeps'

export class HttpThreeDGenApi implements ThreeDGenApi {
  private readonly appId: string
  private readonly deps: BackendHttpDeps

  constructor(appId: string, deps: BackendHttpDeps) {
    this.appId = appId
    this.deps = deps
  }

  async generate(params: ThreeDGenParams): Promise<ThreeDGenResult> {
    return this.post<ThreeDGenResult>('sync-generate', params)
  }

  async remesh(params: RemeshParams): Promise<RemeshResult> {
    return this.post<RemeshResult>('sync-remesh', params)
  }

  async rig(params: RigParams): Promise<RigResult> {
    return this.post<RigResult>('sync-rig', params)
  }

  async animate(params: AnimateParams): Promise<AnimateResult> {
    return this.post<AnimateResult>('sync-animate', params)
  }

  async getCompletedJobs(): Promise<ThreeDGenJobEvent[]> {
    return []
  }

  private async post<T>(endpoint: string, body: unknown): Promise<T> {
    const idToken = await this.deps.getIdToken()
    const baseUrl = this.deps.getCloudRunUrl()
    const url = `${baseUrl}/v1/threedgen/${this.appId}/${endpoint}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    }

    const gst = await resolveGameSessionToken(this.deps.getGameSessionToken);
    if (gst) headers['x-h5-game-session'] = gst;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const errorText = await response.text()
      let errorMessage = `3D generation failed (${response.status})`
      let errorCode = 'UNKNOWN'
      let detail: string | undefined
      try {
        const errorJson = JSON.parse(errorText) as Record<string, unknown>
        errorMessage = (errorJson.error ?? errorJson.message ?? errorMessage) as string
        if (typeof errorJson.code === 'string') errorCode = errorJson.code
        if (typeof errorJson.detail === 'string') detail = errorJson.detail
      } catch {
        if (errorText) errorMessage = errorText
      }
      throw new RundotApiError(errorCode, errorMessage, response.status, detail)
    }

    return response.json()
  }
}
